import {
  MicrophoneIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { Trans } from "@lingui/react/macro";

import { usePokeWithAudio } from "~/hook/overlay/usePokeWithAudio.ts";
import { createDraggableSheet } from "~/ui/base/DraggableSheet.tsx";

export const PokeWithAudioSheet = createDraggableSheet<{ email: string }>(
  ({ close, context: { email }, titleId }) => {
    const {
      audioBlob,
      disabled,
      formattedDuration,
      handlePlayTest,
      handleRetake,
      handleSend,
      handleToggleRecord,
      micPermissionError,
      playing,
      recording,
    } = usePokeWithAudio(email, close);

    return (
      <div className="p-6" data-testid="소리 찌르기">
        <h1
          className="text-lg font-semibold text-zinc-800 dark:text-zinc-100"
          id={titleId}
        >
          <Trans>소리 찌르기 🎙️</Trans>
        </h1>
        <div className="h-6" />

        {/* Microphone / Record Button & Progress */}
        <div className="flex flex-col items-center justify-center rounded-2xl bg-zinc-50 py-12 dark:bg-zinc-900">
          {!audioBlob ? (
            <div className="flex flex-col items-center">
              <button
                className={`relative flex h-28 w-28 items-center justify-center rounded-full text-white transition-all duration-300 ${
                  recording
                    ? "scale-110 bg-red-500"
                    : "bg-black hover:bg-zinc-800 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                } disabled:opacity-50`}
                disabled={disabled}
                onClick={handleToggleRecord}
                type="button"
              >
                {recording ? (
                  <div className="h-8 w-8 rounded-md bg-white" />
                ) : (
                  <MicrophoneIcon className="h-12 w-12" />
                )}
                {recording && (
                  <div className="absolute inset-0 scale-125 animate-ping rounded-full border-4 border-red-500 opacity-30" />
                )}
              </button>
              <p className="mt-6 text-sm font-medium text-zinc-600 dark:text-zinc-400">
                {recording ? (
                  <Trans>녹음 중... ({formattedDuration}s)</Trans>
                ) : micPermissionError ? (
                  <Trans>
                    마이크가 허용되지 않았습니다. 설정을 확인해 주세요.
                  </Trans>
                ) : (
                  <Trans>버튼을 눌러 녹음을 시작하세요 (최대 3초)</Trans>
                )}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <button
                className="flex h-28 w-28 items-center justify-center rounded-full bg-blue-500 text-white transition-all hover:bg-blue-600 active:scale-95"
                onClick={handlePlayTest}
                type="button"
              >
                {playing ? (
                  <PauseIcon className="h-12 w-12" />
                ) : (
                  <PlayIcon className="h-12 w-12 pl-2" />
                )}
              </button>
              <p className="mt-6 text-sm font-medium text-zinc-600 dark:text-zinc-400">
                {playing ? (
                  <Trans>재생 중...</Trans>
                ) : (
                  <Trans>녹음된 소리 들어보기 ({formattedDuration}s)</Trans>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="pt-6">
          {audioBlob && (
            <div className="flex gap-3">
              <button
                className="dark:active:bg-zinc-750 h-12 flex-1 rounded-2xl bg-zinc-100 font-medium text-zinc-900 duration-300 active:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-100"
                disabled={disabled}
                onClick={handleRetake}
                type="button"
              >
                <Trans>다시 녹음</Trans>
              </button>
              <button
                className="h-12 flex-1 rounded-2xl bg-black font-medium text-white duration-300 active:bg-zinc-300 disabled:bg-zinc-300 dark:bg-zinc-800 dark:active:bg-zinc-700 dark:disabled:bg-zinc-900"
                data-testid="찌르기 버튼"
                disabled={disabled}
                onClick={() => void handleSend()}
                type="button"
              >
                <Trans>찌르기</Trans> 👉
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);
